import type { Message } from '@liniya/api-client';
import { QUICK_REACTIONS } from '@liniya/client-core';
import { CheckSquare, Copy, Flag, Forward, Pencil, Reply, Trash2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';



export interface MenuTarget {
  message: Message;
  anchor: { x: number; y: number };
}

interface Props {
  target: MenuTarget;
  own: boolean;
  canReply: boolean;
  canEdit: boolean;
  /** Whether this message may be chosen for deletion — the same rule the
   * list applies, so the menu cannot offer what the list will refuse. */
  canSelect: boolean;
  onClose: () => void;
  onReact: (emoji: string) => void;
  onReply: () => void;
  onForward: () => void;
  onCopy: () => void;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onReport: () => void;
}

/**
 * Actions for one message.
 *
 * Positioned at the pointer but clamped to the viewport, so a right-click near
 * an edge does not open a menu that is half off-screen — which on a phone
 * would be most of them.
 */
export function MessageContextMenu({
  target,
  own,
  canReply,
  canEdit,
  canSelect,
  onClose,
  onReact,
  onReply,
  onForward,
  onCopy,
  onSelect,
  onEdit,
  onDelete,
  onReport,
}: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(target.anchor);

  useLayoutEffect(() => {
    const node = panel.current;
    if (!node) return;
    const { offsetWidth, offsetHeight } = node;
    setPosition({
      x: Math.max(8, Math.min(target.anchor.x, globalThis.innerWidth - offsetWidth - 8)),
      y: Math.max(8, Math.min(target.anchor.y, globalThis.innerHeight - offsetHeight - 8)),
    });
  }, [target.anchor]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!panel.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={panel}
      className="context-menu"
      role="menu"
      aria-label="Действия с сообщением"
      style={{ left: position.x, top: position.y }}
    >
      <div className="quick-reactions" aria-label="Быстрые реакции">
        {QUICK_REACTIONS.map((emoji) => (
          <button key={emoji} type="button" onClick={() => onReact(emoji)} aria-label={emoji}>
            {emoji}
          </button>
        ))}
      </div>
      <div className="menu-actions">
        {canReply ? (
          <button type="button" role="menuitem" onClick={onReply}>
            <Reply size={19} /> <span>Ответить</span>
          </button>
        ) : null}
        <button type="button" role="menuitem" onClick={onForward}>
          <Forward size={19} /> <span>Переслать</span>
        </button>
        <button type="button" role="menuitem" onClick={onCopy}>
          <Copy size={19} /> <span>Копировать</span>
        </button>
        {/*
          Entering selection from here rather than from a modifier-click: the
          menu is already open, and the person who wants to delete several has
          just right-clicked the first of them. Offered only on messages this
          viewer may delete, which is the only reason to select anything.
        */}
        {canSelect ? (
          <button type="button" role="menuitem" onClick={onSelect}>
            <CheckSquare size={19} /> <span>Выбрать</span>
          </button>
        ) : null}
        {own && canEdit ? (
          <button type="button" role="menuitem" onClick={onEdit}>
            <Pencil size={19} /> <span>Изменить</span>
          </button>
        ) : null}
        {!own ? (
          <button type="button" role="menuitem" className="danger" onClick={onReport}>
            <Flag size={19} /> <span>Пожаловаться</span>
          </button>
        ) : null}
        {own ? (
          <button type="button" role="menuitem" className="danger" onClick={onDelete}>
            <Trash2 size={19} /> <span>Удалить</span>
          </button>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
